/*
 * Truncate text to fit within a given width. Add an ellipsis as suffix if the text exceeds the max width.
 * This is meant to be used for text labels in SVG charts. For HTML labels, use text-overflow: ellipsis.
 */

// Create a single canvas and context for measuring all text widths.
const canvas = document.createElement('canvas');
const context = canvas.getContext('2d');

const LEGACY_CHART_FONT_FAMILY = '"Source Sans Pro", sans-serif';
const THEMED_CHART_FONT_FAMILY = '"Lay Grotesk", "Roboto", sans-serif';

/**
 * The family chart labels actually render in: the CSS modules resolve
 * `var(--ui-font-family-sans, "Source Sans Pro")`, which the themed
 * appearance re-points to Lay Grotesk. Measuring with the same family keeps
 * the allocated width honest — Lay Grotesk runs wider than Source Sans Pro,
 * so measuring the legacy font under a theme clips labels.
 */
export const getDefaultChartFontFamily = (): string =>
  typeof document !== 'undefined' &&
  document.body?.getAttribute('data-theme') !== null
    ? THEMED_CHART_FONT_FAMILY
    : LEGACY_CHART_FONT_FAMILY;

/**
 * A web font that has not finished loading measures as its fallback; caching
 * that poisons every later lookup. Measure uncached until the family is
 * available (jsdom has no document.fonts — treat as loaded).
 */
const isFontFamilyLoaded = (fontFamily: string, fontSize: number): boolean => {
  if (typeof document === 'undefined' || document.fonts === undefined) {
    return true;
  }
  try {
    return document.fonts.check(`${fontSize}px ${fontFamily}`);
  } catch {
    return true;
  }
};

/** ================================
 *  Get Text Width
 *  ================================ */
// Cache text width and truncated text for each combination of text, font family, and font size.
const textWidthCache = new Map<string, number>();

type GetTextWidthProps = {
  text: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: number | string;
};
export const getTextWidth = ({
  text,
  fontSize = 11,
  fontFamily = getDefaultChartFontFamily(),
  fontWeight,
}: GetTextWidthProps) => {
  if (!context) return null;

  context.font = `${fontWeight !== undefined ? `${fontWeight} ` : ''}${fontSize}px ${fontFamily}`;
  const textWidthCacheKey = `${text}|${fontFamily}|${fontSize}|${fontWeight ?? ''}`;

  const cached = textWidthCache.get(textWidthCacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const textWidth = context.measureText(text).width;
  if (isFontFamilyLoaded(fontFamily, fontSize)) {
    textWidthCache.set(textWidthCacheKey, textWidth);
  }

  return textWidth;
};

/** ================================
 *  Get Text Height
 *  ================================ */
// Cache text height for each combination of text, font family, and font size.
const textHeightCache = new Map<string, number>();

type GetTextHeightProps = {
  text: string;
  fontSize?: number;
  fontFamily?: string;
};
export const getTextHeight = ({
  text,
  fontSize = 11,
  fontFamily = getDefaultChartFontFamily(),
}: GetTextHeightProps) => {
  if (!context) return null;

  context.font = `${fontSize}px ${fontFamily}`;
  const textHeightCacheKey = `${text}|${fontFamily}|${fontSize}`;

  const cached = textHeightCache.get(textHeightCacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const metrics = context.measureText(text);
  const textHeight =
    metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
  if (isFontFamilyLoaded(fontFamily, fontSize)) {
    textHeightCache.set(textHeightCacheKey, textHeight);
  }

  return textHeight;
};

/** ================================
 *  Get Max Text Width
 *  ================================ */
export const getMaxTextWidth = ({
  texts,
  fontSize = 11,
  fontFamily = getDefaultChartFontFamily(),
}: {
  texts: string[];
  fontSize?: number;
  fontFamily?: string;
}) => {
  const widths = texts
    .map((text) => getTextWidth({ text, fontSize, fontFamily }))
    .filter((width) => width !== null);

  if (widths.length === 0) return 0;

  return Math.max(...widths);
};

/** ================================
 *  Truncate text
 *  ================================ */
export const TRUNCATION_ELLIPSIS = '…';

const truncatedTextCache = new Map<string, TruncateTextResult>();

type TruncateTextProps = { maxWidth: number } & GetTextWidthProps;

export type TruncateTextResult = {
  text: string;
  /** False when not even one character of the source text fit. */
  fitted: boolean;
};

export const truncateTextWithFit = ({
  text,
  maxWidth,
  fontSize = 11,
  fontFamily = getDefaultChartFontFamily(),
  fontWeight,
}: TruncateTextProps): TruncateTextResult => {
  if (typeof text !== 'string') {
    text = String(text ?? '');
  }
  text = text.trim();
  if (!context) return { text, fitted: true };

  const truncatedTextCacheKey = `${text}|${maxWidth}|${fontFamily}|${fontSize}|${fontWeight ?? ''}`;
  const cached = truncatedTextCache.get(truncatedTextCacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const textWidth = getTextWidth({ text, fontSize, fontFamily, fontWeight });
  if (!textWidth) return { text, fitted: true };

  let result: TruncateTextResult = { text, fitted: true };

  if (textWidth > maxWidth) {
    let low = 0;
    let high = text.length;

    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      const truncated = text.slice(0, mid).trim() + TRUNCATION_ELLIPSIS;
      const truncatedWidth = context.measureText(truncated).width;

      if (truncatedWidth <= maxWidth) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }

    result = {
      text: text.slice(0, low).trim() + TRUNCATION_ELLIPSIS,
      fitted: low > 0,
    };
  }

  if (isFontFamilyLoaded(fontFamily, fontSize)) {
    truncatedTextCache.set(truncatedTextCacheKey, result);
  }
  return result;
};

export const truncateText = (props: TruncateTextProps): string =>
  truncateTextWithFit(props).text;
